import React, { FC, useCallback } from 'react'
import { useAnalyticsHandlers, analytics } from '@moonpig/launchpad-analytics'
import { styled } from '@moonpig/launchpad-utils'
import { Box, SideModal } from '@moonpig/launchpad-components'
import { SortOrder } from '@moonpig/web-explore-types-graphql'
import {
  createGenericSelectContentEvent,
  trackGAEvent,
} from '@moonpig/web-core-analytics'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import {
  sanitizeContentType,
  useSearchAnalytics,
} from '../../pages/SearchPage/analytics'
import {
  filterCategoryEvent,
  filtersMenuEvent,
  milestoneResultsEvent,
} from '../../pages/SearchPage/events'
import {
  DESKTOP_MODAL_FILTERS_MENU_WIDTH,
  FILTERS_MENU_OFFSET,
  FILTERS_MENU_RESULTS_CTA_CONTAINER_HEIGHT,
} from '../../constants'
import {
  Header,
  SlidingMenu,
  FilterCategorySubMenu,
  SortBy,
  ResultsButton,
  AccordionMenu,
} from './components'
import {
  FilterCategory,
  IntegerFilter,
  FilterItem,
} from '../../services/types/services'
import { mapFilterItem } from '../../utils/GalleryFiltering/mappings'
import { useSearchStore } from '../../store/SearchStore'
import { useFindLocaleText } from '../../text-localisation'
import { FilterSource } from '../../types'
import { shouldShowClearButton } from '../../utils/GalleryFiltering/shouldShowClearButton'
import {
  IntegerFilterInput,
  useIntegerInputFilter,
  IntegerFieldTypes,
} from '../IntegerFilterInput'

const StyledOffsetContainer = styled(Box)`
  margin-top: ${FILTERS_MENU_OFFSET}px;
  height: 100%;
  max-height: calc(
    100vh - ${FILTERS_MENU_OFFSET + FILTERS_MENU_RESULTS_CTA_CONTAINER_HEIGHT}px
  );
  overflow: hidden auto;
  overscroll-behavior: contain;

  @supports (max-height: 100dvh) {
    max-height: calc(
      100dvh -
        ${FILTERS_MENU_OFFSET + FILTERS_MENU_RESULTS_CTA_CONTAINER_HEIGHT}px
    );
  }
`

const StyledScrollableArea = styled(Box)`
  position: relative;
  overflow: hidden auto;
  overscroll-behavior: contain;
  min-height: calc(
    100vh - ${FILTERS_MENU_OFFSET + FILTERS_MENU_RESULTS_CTA_CONTAINER_HEIGHT}px
  );

  @supports (min-height: 100dvh) {
    min-height: calc(
      100dvh -
        ${FILTERS_MENU_OFFSET + FILTERS_MENU_RESULTS_CTA_CONTAINER_HEIGHT}px
    );
  }
`

const hasSubCategories = (category: FilterCategory) => {
  return category.children.some(child => child.__typename === 'FilterCategory')
}

type FiltersMenuProps = {
  sortOptions: { label: string; value: string }[]
}

export const FiltersMenu: FC<FiltersMenuProps> = ({ sortOptions }) => {
  const isLaunchpadAnalytics = useSearchAnalytics()

  const localiseText = useFindLocaleText()
  const isOpen = useSearchStore(store => store.filtersMenuOpen)
  const pageContext = useSearchStore(store => store.pageContext)
  const filters = useSearchStore(store => store.filters)
  const toggleFiltersMenu: () => void = useSearchStore(
    store => store.toggleFiltersMenu,
  )
  const toggleFiltersCategory = useSearchStore(
    store => store.toggleFiltersCategory,
  )
  const onIntegerFilterChange = useSearchStore(
    store => store.onIntegerFilterChange,
  )
  const integerFilters = useSearchStore(store => store.integerFilters)
  const categories = useSearchStore(store => store.selectedCategories)
  const toggleFilter = useSearchStore(store => store.toggleFilter)
  const applyIntegerFilter = useSearchStore(store => store.applyIntegerFilter)
  const resultsLoading = useSearchStore(store => store.loading)
  const resultsCount = useSearchStore(store => store.results.totalCount)
  const sortBy = useSearchStore(store => store.sortBy)
  const sortValue = useSearchStore(store => store.sortValue)
  const defaultSortValue = useSearchStore(store => store.initialSortValue)
  const sortMenuOpen = useSearchStore(store => store.sortMenuOpen)
  const selectedFilters = useSearchStore(store => store.selectedFilters)

  const menuAction = isOpen ? 'filters closed' : 'filters opened'

  // Legacy event: apps/explore/find/src/store/middleware/trackEvent.ts
  const menuClickEvent = filtersMenuEvent()
    .withEventData({
      category: `${pageContext.type} action`.toLowerCase(),
      action: menuAction,
      label: pageContext.title.toLowerCase(),
    })
    .withContentData({
      content_type: sanitizeContentType(
        `${pageContext.type} action | ${menuAction} | ${pageContext.title}`,
      ),
    })

  const useAgeSelectionUx =
    useExperiment('search-use-age-selection-ux-v2', {
      fallback: 'control',
    })?.toLowerCase() === 'variant'

  const currentCategory = categories[categories.length - 1]

  const isIntegerFilter =
    useAgeSelectionUx && currentCategory?.__typename === 'IntegerFilter'

  const integerFilter = isIntegerFilter
    ? integerFilters.find(f => f.integerId === currentCategory?.integerId)
    : undefined

  const integerFilterValue = integerFilter?.value || null

  const { createConfiguration, isIntegerFilterValueValid } =
    useIntegerInputFilter(integerFilter)

  const applyAgeFilter = (filter: IntegerFilter) => {
    applyIntegerFilter({
      filter: {
        ...filter,
        selectedValue: integerFilterValue,
      },
      value: integerFilterValue,
      source: FilterSource.FiltersMenu,
      filterId: filter.id,
    })
    if (isLaunchpadAnalytics) analytics.send(menuClickEvent)
    toggleFiltersMenu()
  }

  const milestoneLabel = integerFilterValue?.toString() || ''

  // Legacy event: apps/explore/find/src/store/middleware/trackEvent.ts
  const milestoneSubmitEvent = milestoneResultsEvent()
    .withEventData({
      category: `${pageContext.type} action`.toLowerCase(),
      label: milestoneLabel,
    })
    .withContentData({
      content_type:
        `${pageContext.type} action | show milestone results | ${milestoneLabel}`
          .toLowerCase()
          .substring(0, 100),
    })

  const ageSubmitHandlers = useAnalyticsHandlers<HTMLFormElement>(
    isLaunchpadAnalytics && isIntegerFilterValueValid(integerFilterValue)
      ? { onSubmit: milestoneSubmitEvent }
      : {},
    {
      onSubmit: e => {
        e.preventDefault()
        if (isIntegerFilterValueValid(integerFilterValue)) {
          applyAgeFilter(currentCategory as IntegerFilter)
        }
      },
    },
  )

  const filterItems = ({
    category,
    index,
  }: {
    category: FilterCategory | IntegerFilter
    index: number
  }) => {
    if (useAgeSelectionUx && category.__typename === 'IntegerFilter') {
      return (
        <Box p={6}>
          <form {...ageSubmitHandlers}>
            <IntegerFilterInput
              onChange={(ageInput: string) =>
                onIntegerFilterChange({
                  integerFilterId: category.integerId,
                  value: Number(ageInput) || /* istanbul ignore next */ null,
                })
              }
              input={createConfiguration(
                IntegerFieldTypes.age,
                integerFilterValue,
              )}
              hasValidValue={isIntegerFilterValueValid(integerFilterValue)}
            />
          </form>
        </Box>
      )
    }
    if (category.__typename === 'FilterCategory') {
      return category.children.map(filterItem =>
        mapFilterItem({
          filterItem,
          onClick: onFilterItemClick,
          level: index + 1,
        }),
      )
    }
  }

  const hideTopLevelClearButton = !shouldShowClearButton({
    selectedFilters,
    sortValue,
    defaultSortValue,
    integerFilters,
  })

  const onFilterItemClick = useCallback(
    (filterItem: FilterItem, select?: boolean) => {
      switch (filterItem.__typename) {
        case 'FilterCategory':
        case 'IntegerFilter':
          if (select === undefined) {
            toggleFiltersCategory(filterItem)
          } else {
            toggleFilter({
              filter: filterItem,
              select: !!select,
              source: FilterSource.FiltersMenu,
            })
          }
          break
        case 'Filter':
          toggleFilter({
            filter: filterItem,
            select: !!select,
            source: FilterSource.FiltersMenu,
          })
          break
        case 'FilterToggle':
        case 'FilterInvertedToggle':
          toggleFilter({
            filter: filterItem,
            select: !!select,
            source: FilterSource.FiltersMenu,
          })
          break
      }
    },
    [toggleFilter, toggleFiltersCategory],
  )

  const onSortByChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      sortBy(event.target.value as SortOrder)
    },
    [sortBy],
  )

  const ResultsCTA = (
    <Box borderTop={1} borderColor="colorBorder03">
      <ResultsButton
        events={
          isLaunchpadAnalytics
            ? {
                onClick: isIntegerFilter
                  ? milestoneSubmitEvent
                  : menuClickEvent,
              }
            : undefined
        }
        onClick={
          isIntegerFilter
            ? () => applyAgeFilter(currentCategory as IntegerFilter)
            : toggleFiltersMenu
        }
        loading={resultsLoading}
        resultsCount={resultsCount}
        label={
          isIntegerFilter ? localiseText('find.age_input.apply') : undefined
        }
        disabled={
          isIntegerFilter &&
          (integerFilterValue === null ||
            !isIntegerFilterValueValid(integerFilterValue))
        }
      />
    </Box>
  )

  return isOpen ? (
    <SideModal
      onDismiss={() => {
        if (isLaunchpadAnalytics) analytics.send(menuClickEvent)
        toggleFiltersMenu()
      }}
      isOpen={isOpen}
      description={localiseText('find.filter_menu')}
      side="left"
      maxWidth={{ md: DESKTOP_MODAL_FILTERS_MENU_WIDTH }}
      minWidth={{ md: DESKTOP_MODAL_FILTERS_MENU_WIDTH }}
    >
      <SlidingMenu>
        <Header
          onClose={toggleFiltersMenu}
          hideClearButton={hideTopLevelClearButton}
          events={
            isLaunchpadAnalytics ? { onClick: menuClickEvent } : undefined
          }
        />
        <StyledOffsetContainer>
          <SortBy
            options={sortOptions}
            value={sortValue}
            onChange={onSortByChange}
            defaultValue={defaultSortValue}
            open={sortMenuOpen}
            resultsButton={ResultsCTA}
          />
          {filters.map(filterItem =>
            mapFilterItem({ filterItem, onClick: onFilterItemClick }),
          )}
        </StyledOffsetContainer>

        {categories.map((category, index) => (
          <FilterCategorySubMenu
            key={`${category.id}-sub-menu`}
            category={category}
            level={index + 1}
            onClose={() => {
              toggleFiltersCategory(category)
            }}
            hideClearButton={
              !shouldShowClearButton({
                category,
                selectedFilters,
                integerFilters,
              })
            }
          >
            <StyledScrollableArea>
              {category.__typename === 'FilterCategory' &&
              hasSubCategories(category) ? (
                <AccordionMenu
                  category={category}
                  level={index + 1}
                  onClickItem={onFilterItemClick}
                  onClickCategoryItem={({ action, label }) => {
                    // Legacy event: apps/explore/find/src/components/FiltersMenu/FiltersMenu.tsx
                    if (isLaunchpadAnalytics) {
                      analytics.send(
                        filterCategoryEvent()
                          .withEventData({
                            category:
                              `${pageContext.type} action`.toLowerCase(),
                            action,
                            label: label.toLowerCase(),
                          })
                          .withContentData({
                            content_type: sanitizeContentType(
                              `${pageContext.type} action | ${action} | ${label}`,
                            ),
                          }),
                      )
                    } else
                      // Launchpad event: apps/explore/find/src/pages/SearchPage/events.ts
                      trackGAEvent(
                        createGenericSelectContentEvent({
                          eventCategory: `${pageContext.type} action`,
                          eventAction: action,
                          eventLabel: label,
                        }),
                      )
                  }}
                />
              ) : (
                filterItems({ category, index })
              )}
            </StyledScrollableArea>
            {ResultsCTA}
          </FilterCategorySubMenu>
        ))}
        {ResultsCTA}
      </SlidingMenu>
    </SideModal>
  ) : null
}
